import { Simulation } from '../../types';
import { format } from 'date-fns';
import JSZip from 'jszip';
import { toast } from 'sonner';

export class ConversationSimulationExporter {
  private static sanitizeForFilename(value: string): string {
    return value
      .replace(/[/\\:*?"<>|]/g, '-')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private escapeCsvField(value: string): string {
    return value.replace(/\r\n|\r|\n/g, ' ').replace(/"/g, '""');
  }

  private buildCsv(simulation: Simulation): string {
    let csvContent = 'Role,Message\n';
    simulation.messages.forEach((message) => {
      const escaped = this.escapeCsvField(message.content);
      csvContent += `${message.role},"${escaped}"\n`;
    });
    return csvContent;
  }

  /** Agent + Persona + Date + Simulation id */
  private getCsvFilename(simulation: Simulation): string {
    const namePart = ConversationSimulationExporter.sanitizeForFilename(
      simulation.name,
    );
    const datePart = format(
      new Date(simulation.created_at),
      'yyyy-MM-dd-HH-mm',
    );
    return `${namePart}-${datePart}-${simulation.id}.csv`;
  }

  exportOne(simulation: Simulation, e?: React.MouseEvent): void {
    e?.stopPropagation();
    try {
      const csvContent = this.buildCsv(simulation);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', this.getCsvFilename(simulation));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(
        `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  async exportMany(simulations: Simulation[]): Promise<void> {
    if (simulations.length === 0) return;
    try {
      const zip = new JSZip();
      for (const sim of simulations) {
        const csvContent = this.buildCsv(sim);
        const filename = this.getCsvFilename(sim);
        zip.file(filename, csvContent, { binary: false });
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const zipName = `conversations-export-${format(new Date(), 'yyyy-MM-dd-HH-mm')}.zip`;
      link.setAttribute('download', zipName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(
        `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }
}

export const conversationSimulationExporter =
  new ConversationSimulationExporter();
